import { Component, Inject, OnInit, Optional } from "@angular/core";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Fabbricato } from "src/app/models/fabbricato";
import { Polizza } from "src/app/models/polizza";
import { FabbricatoService } from "src/app/services/fabbricato.service";
import { IncaricoService } from "src/app/services/incarico.service";
import { PolizzaService } from "src/app/services/polizza.service";
import { BaseEditForm } from "src/app/shared/baseEditForm";
import { findInRoute } from "src/app/shared/utilities";

@Component({
  selector: "fabbricati-form",
  templateUrl: "./form.component.html",
  styleUrls: ["./form.component.scss"],
})
export class FabbricatiFormComponent extends BaseEditForm implements OnInit {
  fabbricato: Fabbricato;
  form: FormGroup;
  isUpdating = false;
  polizzaSommeAssicurate: Polizza | null = null;
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private fabbricatoService: FabbricatoService,
    private polizzaService: PolizzaService,
    private incaricoService: IncaricoService,
    @Optional() public dialogRef: MatDialogRef<FabbricatiFormComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: Fabbricato
  ) {
    super();
    const incomingData: any = data ?? {};
    this.fabbricato = new Fabbricato().deserialize(incomingData);
    this.polizzaSommeAssicurate = incomingData?.polizza ?? null;
  }

  async ngOnInit() {
    const idFabbricato = findInRoute("idFabbricato", this.route);
    if (idFabbricato) {
      this.fabbricato = await this.fabbricatoService.getOne(+idFabbricato, [
        "comune",
        "comune.provincia",
        "classe_fabbricato",
        "tipologia_costruttiva",
        "stato_manutenzione_generale",
        "stato_manutenzione_impianto_idrico_igenico",
        "polizze",
        "polizze.somme_assicurate",
      ]);
    }
    await this.syncPolizzaSommeAssicurate();
    let current_unita_misura_id =
      this.fabbricato?.unita_misura_estensione_id || 1;
    this.form = this.fb.group({
      id: [this.fabbricato?.id], //needed because aggiornaUbicazioneDescrizione reassigns form value to this.incarico. We can't lose id reference!
      nome: [this.fabbricato?.nome],
      via: [this.fabbricato?.via],
      civico: [this.fabbricato?.civico],
      comune: [this.fabbricato?.comune],
      cap: [this.fabbricato?.cap],
      partita_iva: [this.fabbricato?.partita_iva],
      codice_fiscale: [this.fabbricato?.codice_fiscale],
      IBAN: [this.fabbricato?.IBAN],
      amministratore: [this.fabbricato?.amministratore],
      estensione: [this.fabbricato?.estensione],
      totale_mc: [this.fabbricato?.totale_mc],
      valore_medio: [this.fabbricato?.valore_medio],
      unita_misura_estensione_id: ["" + current_unita_misura_id], //need cast to string because mat-option value attr is string value! if you remove this cast to string, the component won't display the value at all.
      verificato_su_planimetrie: [this.fabbricato?.verificato_su_planimetrie],
      verificato_su_google_earth: [this.fabbricato?.verificato_su_google_earth],
      numero_piani_fuori_terra: [this.fabbricato?.numero_piani_fuori_terra],
      numero_piani_entro_terra: [this.fabbricato?.numero_piani_entro_terra],
      estensione_garage_servizi_piano_terra: [
        this.fabbricato?.estensione_garage_servizi_piano_terra,
      ],
      percentuale_superficie_piani_interrati: [
        this.fabbricato?.percentuale_superficie_piani_interrati,
      ],
      percentuale_uso_civile_uffici: [
        this.fabbricato?.percentuale_uso_civile_uffici,
      ],
      percentuale_uso_commerciale: [
        this.fabbricato?.percentuale_uso_commerciale,
      ],
      tipologia_costruttiva: [this.fabbricato?.tipologia_costruttiva],
      anno_costruzione: [this.fabbricato?.anno_costruzione],
      anno_ristrutturazione: [this.fabbricato?.anno_ristrutturazione],
      classe_fabbricato: [this.fabbricato?.classe_fabbricato],
      stato_manutenzione_generale: [
        this.fabbricato?.stato_manutenzione_generale,
      ],
      stato_manutenzione_impianto_idrico_igenico: [
        this.fabbricato?.stato_manutenzione_impianto_idrico_igenico,
      ],
      altri_valori: [this.fabbricato?.altri_valori],
      costo_unitario: [this.fabbricato?.costo_unitario],
      degrado_percentuale: [this.fabbricato?.degrado_percentuale],
      preesistenza: this.fb.array([]),
      note: [this.fabbricato?.note],
    });

    this.setupNomeCapitalization();
    this.setupViaCapitalization();
    this.form.valueChanges.subscribe((v) =>
      this.aggiornaUbicazioneDescrizione(v)
    );

    this.initializePreesistenza();
    this.subscribeToFabbricatoFieldChanges();
  }

  private setupNomeCapitalization(): void {
    const nomeControl = this.form.get("nome");
    if (!nomeControl) return;

    nomeControl.valueChanges.subscribe((value) => {
      if (typeof value !== "string") return;
      const formatted = value.toUpperCase();
      if (formatted !== value) {
        nomeControl.setValue(formatted, { emitEvent: false });
      }
    });
  }

  private setupViaCapitalization(): void {
    const viaControl = this.form.get("via");
    if (!viaControl) return;

    viaControl.valueChanges.subscribe((value) => {
      if (typeof value !== "string") return;
      const formatted = this.capitalizeEachWord(value);
      if (formatted !== value) {
        viaControl.setValue(formatted, { emitEvent: false });
      }
    });
  }

  aggiornaUbicazioneDescrizione(v) {
    this.fabbricato = new Fabbricato().deserialize(v);
  }

  async aggiornaIndirizzoDaUltimoIncarico() {
    if (!this.fabbricato?.id) return;
    try {
      this.isUpdating = true;
      const updated =
        await this.fabbricatoService.aggiornaIndirizzoPreesistenza(
          this.fabbricato.id
        );
      if (updated) {
        Object.assign(this.fabbricato, updated);
        this.form.patchValue({
          via: updated.via ?? null,
          civico: updated.civico ?? null,
          cap: updated.cap ?? null,
          comune: updated.comune ?? null,
        });
        this.toast.success("Indirizzo preso dall'ultimo incarico");
      }
    } catch (e: any) {
      console.error(e);
      const status = e?.status;
      const serverMsg = e?.error?.message || e?.message;
      if (serverMsg) {
        this.toast.error(serverMsg);
      } else if (status === 404) {
        this.toast.error("Fabbricato non trovato o nessun incarico collegato.");
      } else {
        this.toast.error(
          "Impossibile aggiornare l'indirizzo della preesistenza"
        );
      }
    } finally {
      this.isUpdating = false;
    }
  }

  async handleSuccessfulSubmit() {
    let res = null;

    try {
      //edit existing agenzia or create new one
      if (this.fabbricato?.id) {
        const payload = this.buildPayloadWithNumericPreesistenza();
        Object.assign(this.fabbricato, payload);
        res = await this.fabbricatoService.edit(this.fabbricato);
      } else {
        const payload = this.buildPayloadWithNumericPreesistenza();
        res = await this.fabbricatoService.create(payload);
      }
    } catch (e) {
      this.toast.error("Impossibile salvare il fabbricato");
      console.error(e);
    }

    this.toast.success(`Fabbricato salvato`);
    return res;
  }

  private buildPayloadWithNumericPreesistenza() {
    const raw = this.form.value;
    let preesistenza = raw?.preesistenza;
    if (Array.isArray(preesistenza)) {
      preesistenza = preesistenza.map((r: any) => ({
        ...r,
        quantita: this.parseItNumber(r?.quantita),
        prezzo: this.parseItNumber(r?.prezzo),
        totale: this.parseItNumber(r?.totale),
        overridePrezzo: r?.overridePrezzo ?? false,
      }));
    }
    const hasTotaleMc =
      raw?.totale_mc !== null && raw?.totale_mc !== undefined && raw?.totale_mc !== "";
    const hasValoreMedio =
      raw?.valore_medio !== null &&
      raw?.valore_medio !== undefined &&
      raw?.valore_medio !== "";

    const totale_mc = hasTotaleMc
      ? Math.round(this.parseItNumber(raw?.totale_mc))
      : null;
    const valore_medio = hasValoreMedio
      ? Math.round(this.parseItNumber(raw?.valore_medio) * 100) / 100
      : null;

    return { ...raw, preesistenza, totale_mc, valore_medio };
  }

  private async syncPolizzaSommeAssicurate() {
    let candidate =
      (await this.tryLoadPolizzaFromIncarico()) ??
      this.polizzaSommeAssicurate ??
      this.extractPolizzaForSomme(this.fabbricato);

    if ((!candidate || !candidate.id) && this.fabbricato?.id) {
      candidate = await this.loadPolizzaByFabbricatoId(this.fabbricato.id);
    }

    if (!candidate?.id) {
      this.polizzaSommeAssicurate = null;
      return;
    }

    try {
      this.polizzaSommeAssicurate = await this.ensureSommeAssicurateLoaded(candidate);
    } catch (e) {
      console.error("Impossibile caricare le somme assicurate collegate al fabbricato", e);
    }
  }

  private async ensureSommeAssicurateLoaded(polizza: Polizza): Promise<Polizza> {
    const somme = (polizza as any)?.somme_assicurate;
    if (Array.isArray(somme)) {
      return polizza;
    }

    return await this.polizzaService.getOne(polizza.id, ["somme_assicurate"]);
  }

  private async tryLoadPolizzaFromIncarico(): Promise<Polizza | null> {
    const idIncarico = findInRoute("idIncarico", this.route);
    if (!idIncarico) return null;

    try {
      const incarico = await this.incaricoService.getOne(+idIncarico, [
        "polizza",
        "polizza.somme_assicurate",
      ]);
      return incarico?.polizza ?? null;
    } catch (e) {
      console.error("Impossibile caricare la polizza dell'incarico", e);
      return null;
    }
  }

  private extractPolizzaForSomme(
    fabbricato: Fabbricato | null | undefined
  ): Polizza | null {
    if (!fabbricato) return null;
    const directPolizza = (fabbricato as any)?.polizza;
    if (directPolizza) {
      return directPolizza as Polizza;
    }
    if (Array.isArray(fabbricato.polizze) && fabbricato.polizze.length > 0) {
      return fabbricato.polizze[0];
    }
    return null;
  }

  private async loadPolizzaByFabbricatoId(fabbricatoId: number): Promise<Polizza | null> {
    try {
      const polizze = await this.polizzaService.getAll([], {
        search: { fabbricato_id: fabbricatoId },
        desiresPagination: false,
      });
      return Array.isArray(polizze) && polizze.length > 0 ? polizze[0] : null;
    } catch (e) {
      console.error("Impossibile caricare la polizza collegata al fabbricato", e);
      return null;
    }
  }

  // --- Preesistenza (Perizia Smart style) ---
  get preesistenzaFormArray(): FormArray {
    return this.form.get("preesistenza") as FormArray;
  }

  get totaleMcDisplay(): number | null {
    const value = this.form?.get("totale_mc")?.value;
    return value === null || value === undefined ? null : value;
  }

  get valoreMedioDisplay(): number | null {
    const value = this.form?.get("valore_medio")?.value;
    return value === null || value === undefined ? null : value;
  }

  private initializePreesistenza(): void {
    // If fabbricato has stored preesistenza JSON, load it; else build defaults
    const existing = this.fabbricato?.preesistenza;
    if (Array.isArray(existing) && existing.length) {
      // Load existing rows respecting structure
      this.preesistenzaFormArray.clear();
      existing.forEach((voce: any, index: number) => {
        const group = this.createPreesistenzaFormGroup(index);
        group.patchValue({
          nome: voce?.nome ?? "",
          unitaMisura: this.normalizeUnitaMisura(
            voce?.unitaMisura ?? this.fabbricato?.nome_unita_misura_estensione
          ),
          quantita: voce?.quantita ?? null,
          prezzo: voce?.prezzo ?? null,
          totale: voce?.totale ?? null,
          isDegrado: voce?.isDegrado ?? index === 6,
          overridePrezzo: voce?.overridePrezzo ?? false,
        });
        this.preesistenzaFormArray.push(group);
      });
      this.setupPreesistenzaCalculations();
      this.setupAutomaticPriceCalculations();
      this.calculateRow5Total();
      this.calculateRow6Discounted();
      this.updateDerivedMetrics();
      return;
    }

    // Build default 7 rows like Perizia Smart
    const defaultLabels = [
      "Abitazioni/Uffici",
      "Commerciale",
      "Garage/Servizi a P.T.",
      "Piani entro terra",
      "Totale costi costruzione (escl. oneri professionali, finanziari e terreno)",
      "Valore a nuovo",
      "Valore allo stato d'uso (degrado)",
    ];

    for (let i = 0; i < 7; i++) {
      const group = this.createPreesistenzaFormGroup(i);
      const quantita = this.formulaQuantitaMap(i);
      const prezzo = this.formulaPrezzoMap(i);
      let totale = 0;
      if (i < 5) {
        totale = (quantita || 0) * (prezzo || 0);
      }
      let unitaMisura = "";
      if (i < 4) {
        unitaMisura = this.normalizeUnitaMisura(
          this.fabbricato?.nome_unita_misura_estensione
        );
      }
      group.patchValue({
        nome: defaultLabels[i],
        unitaMisura,
        quantita,
        prezzo,
        totale,
        isDegrado: i === 6,
      });
      this.preesistenzaFormArray.push(group);
    }
    this.setupPreesistenzaCalculations();
    this.setupAutomaticPriceCalculations();
    // Trigger initial totals
    this.calculateRow5Total();
    this.calculateRow6Discounted();
    this.updateDerivedMetrics();
  }

  private subscribeToFabbricatoFieldChanges(): void {
    // Helpers
    const setPreRow = (
      index: number,
      key: "quantita" | "prezzo" | "unitaMisura" | "totale",
      value: any
    ) => {
      const grp = this.preesistenzaFormArray.at(index) as FormGroup;
      if (grp && grp.get(key))
        grp.get(key)!.setValue(value, { emitEvent: true });
    };

    // costo_unitario -> prezzo riga 0 (Abitazioni/Uffici)
    this.form.get("costo_unitario")?.valueChanges.subscribe((v) => {
      setPreRow(0, "prezzo", Number(v) || 0);
    });

    // degrado_percentuale -> prezzo riga 6 (Valore allo stato d'uso)
    this.form.get("degrado_percentuale")?.valueChanges.subscribe((v) => {
      setPreRow(6, "prezzo", Number(v) || 0);
      this.calculateRow6Discounted();
    });

    // estensione & percentuali -> quantita righe 0 e 1
    const recalcEstensioniUso = () => {
      const estensione = Number(this.form.get("estensione")?.value) || 0;
      const percCivile =
        Number(this.form.get("percentuale_uso_civile_uffici")?.value) || 0;
      const percComm =
        Number(this.form.get("percentuale_uso_commerciale")?.value) || 0;
      if (percComm > 0) {
        setPreRow(0, "quantita", +((estensione * percCivile) / 100).toFixed(2));
        setPreRow(1, "quantita", +((estensione * percComm) / 100).toFixed(2));
      } else {
        // tutto civile se commerciale = 0
        setPreRow(0, "quantita", +estensione.toFixed(2));
        setPreRow(1, "quantita", 0);
      }
      this.calculateRow5Total();
      this.calculateRow6Discounted();
    };
    this.form.get("estensione")?.valueChanges.subscribe(recalcEstensioniUso);
    this.form
      .get("percentuale_uso_civile_uffici")
      ?.valueChanges.subscribe(recalcEstensioniUso);
    this.form
      .get("percentuale_uso_commerciale")
      ?.valueChanges.subscribe(recalcEstensioniUso);

    // estensione_garage_servizi_piano_terra -> quantita riga 2 (Garage/Servizi P.T.)
    this.form
      .get("estensione_garage_servizi_piano_terra")
      ?.valueChanges.subscribe((v) => {
        setPreRow(2, "quantita", Number(v) || 0);
        this.calculateRow5Total();
        this.calculateRow6Discounted();
      });

    // piani -> estensione entro terra (riga 3)
    const recalcEntroTerra = () => {
      const estensione = Number(this.form.get("estensione")?.value) || 0;
      const pfuori =
        Number(this.form.get("numero_piani_fuori_terra")?.value) || 0;
      const pentro =
        Number(this.form.get("numero_piani_entro_terra")?.value) || 0;
      let estSingoloPiano = 0;
      if (pfuori > 0) {
        const cubatura = 70; // come modello
        estSingoloPiano = (cubatura * (estensione / pfuori)) / 100;
      }
      const estEntro = +(pentro * estSingoloPiano).toFixed(2);
      setPreRow(3, "quantita", estEntro);
      this.calculateRow5Total();
      this.calculateRow6Discounted();
    };
    this.form
      .get("numero_piani_fuori_terra")
      ?.valueChanges.subscribe(recalcEntroTerra);
    this.form
      .get("numero_piani_entro_terra")
      ?.valueChanges.subscribe(recalcEntroTerra);
    this.form.get("estensione")?.valueChanges.subscribe(recalcEntroTerra);

    this.form
      .get("unita_misura_estensione_id")
      ?.valueChanges.subscribe((id) => {
        const map: any = { "1": "mc.", "2": "mq.", "3": "kg." };
        const unit = map[String(id)] || "mc.";
        this.syncPreesistenzaUnitaMisura(unit);
      });

    // altri_valori -> riga 4 totale
    this.form.get("altri_valori")?.valueChanges.subscribe((v) => {
      const val = Number(v) || 0;
      setPreRow(4, "totale", +val.toFixed(2));
      this.calculateRow5Total();
      this.calculateRow6Discounted();
    });
  }

  private createPreesistenzaFormGroup(index?: number): FormGroup {
    const grp = this.fb.group({
      nome: "",
      unitaMisura: "",
      quantita: null,
      prezzo: null,
      totale: null,
      isDegrado: false,
      overridePrezzo: false,
    });
    const quantitaControl = grp.get("quantita");
    const prezzoControl = grp.get("prezzo");
    const totaleControl = grp.get("totale");
    const isDegradoControl = grp.get("isDegrado");

    const recalc = () => {
      const q = this.parseItNumber(quantitaControl?.value);
      const p = this.parseItNumber(prezzoControl?.value);
      const divisor = isDegradoControl?.value ? 100 : 1;
      const t = Math.round(((q * p) / divisor) * 100) / 100;
      totaleControl?.setValue(t, { emitEvent: false });
      // Refresh aggregates
      this.calculateRow5Total();
      this.calculateRow6Discounted();
      this.updateDerivedMetrics();
    };

    quantitaControl?.valueChanges.subscribe(recalc);
    prezzoControl?.valueChanges.subscribe(recalc);
    isDegradoControl?.valueChanges.subscribe(recalc);
    return grp;
  }

  private calculateRow5Total(): void {
    if (this.preesistenzaFormArray.length < 6) return;
    let total = 0;
    for (let i = 0; i < 5; i++) {
      total += this.parseItNumber(
        this.preesistenzaFormArray.at(i)?.get("totale")?.value
      );
    }
    this.preesistenzaFormArray
      .at(5)
      ?.get("totale")
      ?.setValue(Math.round(total * 100) / 100, { emitEvent: false });
    this.updateDerivedMetrics();
  }

  private calculateRow6Discounted(): void {
    if (this.preesistenzaFormArray.length < 7) return;
    const valoreANuovo = this.parseItNumber(
      this.preesistenzaFormArray.at(5)?.get("totale")?.value
    );
    const degradoPerc = this.parseItNumber(
      this.preesistenzaFormArray.at(6)?.get("prezzo")?.value
    );
    const discounted =
      Math.round(valoreANuovo * (1 - degradoPerc / 100) * 100) / 100;
    this.preesistenzaFormArray
      .at(6)
      ?.get("totale")
      ?.setValue(discounted, { emitEvent: false });
  }

  private setupPreesistenzaCalculations(): void {
    // row5 listens to totals of rows 0..4
    for (let i = 0; i < 5; i++) {
      this.preesistenzaFormArray
        .at(i)
        ?.get("totale")
        ?.valueChanges.subscribe(() => this.calculateRow5Total());
    }
    // row6 listens to row5 total and row6 prezzo
    this.preesistenzaFormArray
      .at(5)
      ?.get("totale")
      ?.valueChanges.subscribe(() => {
        this.calculateRow6Discounted();
        this.updateDerivedMetrics();
      });
    this.preesistenzaFormArray
      .at(6)
      ?.get("prezzo")
      ?.valueChanges.subscribe(() => this.calculateRow6Discounted());
  }

  private setupAutomaticPriceCalculations(): void {
    const percentages = [0.8, 0.3, 0.5];
    // row0 price drives rows 1..3: 0.8x, 0.3x, 0.5x (unless overridden)
    if (this.preesistenzaFormArray.length > 3) {
      const row0PriceCtrl = this.preesistenzaFormArray.at(0)?.get("prezzo");
      row0PriceCtrl?.valueChanges.subscribe((v: any) => {
        const base = this.parseItNumber(v);
        for (let i = 1; i <= 3; i++) {
          const grp = this.preesistenzaFormArray.at(i) as FormGroup;
          if (grp.get("overridePrezzo")?.value) continue;
          const calc = Math.round(base * percentages[i - 1] * 100) / 100;
          grp.get("prezzo")?.setValue(calc, { emitEvent: false });
          const q = this.parseItNumber(grp.get("quantita")?.value);
          grp.get("totale")?.setValue(Math.round(q * calc * 100) / 100, { emitEvent: false });
        }
        this.calculateRow5Total();
        this.calculateRow6Discounted();
      });

      // Subscribe to manual edits on rows 1-3 prezzo
      for (let i = 1; i <= 3; i++) {
        const grp = this.preesistenzaFormArray.at(i) as FormGroup;
        grp.get("prezzo")?.valueChanges.subscribe((val: any) => {
          if (val === null || val === undefined || ("" + val).trim() === "") {
            // Cleared: reset override and recalculate from row 0
            grp.get("overridePrezzo")?.setValue(false, { emitEvent: false });
            const base = this.parseItNumber(this.preesistenzaFormArray.at(0)?.get("prezzo")?.value);
            const calc = Math.round(base * percentages[i - 1] * 100) / 100;
            grp.get("prezzo")?.setValue(calc, { emitEvent: false });
            const q = this.parseItNumber(grp.get("quantita")?.value);
            grp.get("totale")?.setValue(Math.round(q * calc * 100) / 100, { emitEvent: false });
            this.calculateRow5Total();
            this.calculateRow6Discounted();
          } else {
            const base = this.parseItNumber(this.preesistenzaFormArray.at(0)?.get("prezzo")?.value);
            const expected = Math.round(base * percentages[i - 1] * 100) / 100;
            const current = this.parseItNumber(val);
            if (current !== expected) {
              grp.get("overridePrezzo")?.setValue(true, { emitEvent: false });
            }
          }
        });
      }
    }
  }

  syncPreesistenzaUnitaMisura(selected: string): void {
    const unit = this.normalizeUnitaMisura(selected);
    for (let i = 0; i < this.preesistenzaFormArray.length; i++) {
      const grp = this.preesistenzaFormArray.at(i) as FormGroup;
      if (!grp.get("isDegrado")?.value) {
        grp.get("unitaMisura")?.setValue(unit, { emitEvent: false });
      }
    }
  }

  private updateDerivedMetrics(): void {
    if (!this.form || !this.preesistenzaFormArray?.length) return;

    let totalMc = 0;
    this.preesistenzaFormArray.controls.forEach((ctrl) => {
      totalMc += this.parseItNumber(ctrl.get("quantita")?.value);
    });
    const roundedTotalMc = Math.round(totalMc);
    const totaleMcControl = this.form.get("totale_mc");
    if (
      totaleMcControl &&
      totaleMcControl.value !== roundedTotalMc
    ) {
      totaleMcControl.setValue(roundedTotalMc, { emitEvent: false });
    }

    const valoreANuovo = this.parseItNumber(
      this.preesistenzaFormArray.at(5)?.get("totale")?.value
    );
    const valoreMedio =
      roundedTotalMc > 0
        ? Math.round((valoreANuovo / roundedTotalMc) * 100) / 100
        : null;
    const valoreMedioControl = this.form.get("valore_medio");
    if (valoreMedioControl && valoreMedioControl.value !== valoreMedio) {
      valoreMedioControl.setValue(valoreMedio, { emitEvent: false });
    }
  }

  private normalizeUnitaMisura(unit?: string): string {
    if (!unit) return "mc.";
    const t = ("" + unit).trim().toLowerCase();
    if (t === "mc" || t === "mc.") return "mc.";
    if (t === "mq" || t === "mq.") return "mq.";
    if (t === "kg" || t === "kg.") return "kg.";
    return "mq.";
  }

  private formulaQuantitaMap(index: number) {
    if (!this.fabbricato) return null;
    switch (index) {
      case 0:
        return this.fabbricato.estensione_abitazioni_uffici;
      case 1:
        return this.fabbricato.estensione_commerciale;
      case 2:
        return +this.fabbricato.estensione_garage_servizi_piano_terra;
      case 3:
        return this.fabbricato.estensione_entro_terra;
      default:
        return null;
    }
  }

  private formulaPrezzoMap(index: number) {
    if (!this.fabbricato) return null;
    switch (index) {
      case 0:
        return +this.fabbricato.costo_unitario;
      case 1:
        return this.fabbricato.costo_unitario_commerciale;
      case 2:
        return this.fabbricato.costo_unitario_garage_servizi_piano_terra;
      case 3:
        return this.fabbricato.costo_unitario_entro_terra;
      case 6:
        return this.fabbricato.degrado_percentuale;
      default:
        return null;
    }
  }

  private parseItNumber(val: any): number {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return isNaN(val) ? 0 : val;
    const s = ("" + val).trim();
    if (!s) return 0;
    const normalized = s.replace(/\s/g, "");
    if (normalized.includes(",")) {
      const n = parseFloat(normalized.replace(/\./g, "").replace(",", "."));
      return isNaN(n) ? 0 : n;
    }
    if (normalized.includes(".") && /^\d{1,3}(\.\d{3})+$/.test(normalized)) {
      const n = parseFloat(normalized.replace(/\./g, ""));
      return isNaN(n) ? 0 : n;
    }
    const n = parseFloat(normalized);
    return isNaN(n) ? 0 : n;
  }

  private capitalizeEachWord(value: string): string {
    return value.replace(/\S+/g, (word) => {
      if (this.isRomanNumeral(word)) {
        return word.toUpperCase();
      }
      const firstChar = word.charAt(0).toUpperCase();
      return firstChar + word.slice(1).toLowerCase();
    });
  }

  private isRomanNumeral(token: string): boolean {
    if (token.length < 2) return false;
    const upper = token.toUpperCase();
    const excluded = ["DI", "CI", "LI", "MI"];
    if (excluded.includes(upper)) return false;
    return /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/i.test(
      token
    );
  }
}
